import { config } from "@/app/config";
import {
  useGetUserQuery,
  useRemoveFromOrgMutation,
  useRemoveRoleMutation,
  useResetPasswordMutation,
} from "@/features/users/api/usersApi";
import { AssignOrgMembershipModal } from "@/features/users/components/AssignOrgMembershipModal";
import { AssignRoleModal } from "@/features/users/components/AssignRoleModal";
import { useHasPermission } from "@/shared/auth/usePermissions";
import { ErrorState } from "@/shared/components/ErrorState";
import { FullPageLoader } from "@/shared/components/FullPageLoader";
import { notify } from "@/shared/components/notify";
import { formatDate, formatDateTime } from "@/shared/lib/datetime";
import { Permission } from "@keystone/contracts/shared";
import { type AssignableRole, AssignableRoleSchema } from "@keystone/contracts/users";
import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  Menu,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { useTranslation } from "react-i18next";
import {
  FiArrowLeft,
  FiBriefcase,
  FiExternalLink,
  FiKey,
  FiMoreVertical,
  FiPlus,
  FiTrash2,
  FiUser,
} from "react-icons/fi";
import { Link, useParams } from "react-router";

const ASSIGNABLE_SET = new Set<string>(AssignableRoleSchema.options);

function isAssignable(role: string | null | undefined): role is AssignableRole {
  return !!role && ASSIGNABLE_SET.has(role);
}

export function UserDetailPage() {
  const { t, i18n } = useTranslation("users");
  const { id } = useParams<{ id: string }>();
  const canManage = useHasPermission(Permission.ManageUsers);

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useGetUserQuery(id ?? "", {
    skip: !id,
  });

  const [removeRole, { isLoading: removingRole }] = useRemoveRoleMutation();
  const [removeFromOrg, { isLoading: removingOrg }] = useRemoveFromOrgMutation();
  const [resetPassword] = useResetPasswordMutation();

  const [roleModalOpened, { open: openRoleModal, close: closeRoleModal }] = useDisclosure(false);
  const [orgModalOpened, { open: openOrgModal, close: closeOrgModal }] = useDisclosure(false);

  if (!id) {
    return <ErrorState message={t("errors.detailFailed")} />;
  }
  if (isLoading) return <FullPageLoader />;
  if (error || !user) {
    return <ErrorState message={t("errors.detailFailed")} onRetry={() => void refetch()} />;
  }

  const confirmRemoveRole = (role: AssignableRole) =>
    modals.openConfirmModal({
      title: t("detail.confirmRemoveRole.title"),
      children: <Text size="sm">{t("detail.confirmRemoveRole.body", { role })}</Text>,
      labels: { confirm: t("detail.actions.removeRole"), cancel: t("common:actions.cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await removeRole({ id: user.id, body: { role } }).unwrap();
          notify.success("users:notifications.roleRemoved");
        } catch {
          notify.error("users:errors.roleRemoveFailed");
        }
      },
    });

  const confirmResetPassword = () =>
    modals.openConfirmModal({
      title: t("detail.confirmResetPassword.title"),
      children: (
        <Text size="sm">{t("detail.confirmResetPassword.body", { email: user.email })}</Text>
      ),
      labels: {
        confirm: t("detail.confirmResetPassword.confirm"),
        cancel: t("common:actions.cancel"),
      },
      onConfirm: async () => {
        try {
          await resetPassword({ id: user.id }).unwrap();
          notify.success("users:notifications.passwordResetSent");
        } catch {
          notify.error("users:errors.passwordResetFailed");
        }
      },
    });

  const confirmRemoveOrg = (orgId: string, orgName: string) =>
    modals.openConfirmModal({
      title: t("detail.confirmRemoveOrg.title"),
      children: <Text size="sm">{t("detail.confirmRemoveOrg.body", { org: orgName })}</Text>,
      labels: { confirm: t("detail.actions.removeOrg"), cancel: t("common:actions.cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await removeFromOrg({ id: user.id, body: { orgId } }).unwrap();
          notify.success("users:notifications.orgRemoved");
        } catch {
          notify.error("users:errors.orgRemoveFailed");
        }
      },
    });

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="sm">
          <Anchor component={Link} to="/users" size="sm">
            <Group gap={4} align="center">
              <FiArrowLeft aria-hidden="true" />
              {t("actions.back")}
            </Group>
          </Anchor>
        </Group>
        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <ActionIcon variant="default" size="lg" aria-label={t("detail.actions.menu")}>
              <FiMoreVertical aria-hidden="true" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {canManage && (
              <Menu.Item leftSection={<FiKey aria-hidden="true" />} onClick={confirmResetPassword}>
                {t("detail.actions.resetPassword")}
              </Menu.Item>
            )}
            <Menu.Item
              component="a"
              href={`https://manage.auth0.com/dashboard/ca/${config.auth0TenantId}/users/${encodeURIComponent(user.id)}`}
              target="_blank"
              rel="noreferrer"
              leftSection={<FiExternalLink aria-hidden="true" />}
            >
              {t("detail.openInAuth0")}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Card withBorder padding="lg">
        <Group gap="md" wrap="nowrap" align="flex-start">
          <Avatar size="lg" radius="xl" color="initials" name={user.name || user.email} />
          <Stack gap={2} flex={1}>
            <Title order={3}>{user.name || user.email}</Title>
            <Text c="dimmed" size="sm">
              {user.email}
            </Text>
            <Group gap="lg" mt="xs" wrap="wrap">
              <DetailField
                label={t("detail.fields.lastLogin")}
                value={
                  user.lastLogin
                    ? formatDateTime(user.lastLogin, i18n.language)
                    : t("table.lastLoginNever")
                }
              />
              <DetailField
                label={t("detail.fields.createdAt")}
                value={formatDate(user.createdAt, i18n.language)}
              />
              <DetailField label={t("detail.fields.auth0Id")} value={user.id} mono />
            </Group>
          </Stack>
        </Group>
      </Card>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder padding="lg">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <FiUser aria-hidden="true" />
                <Title order={4}>{t("detail.sections.roles")}</Title>
              </Group>
              {canManage && (
                <Button
                  size="xs"
                  leftSection={<FiPlus aria-hidden="true" />}
                  onClick={openRoleModal}
                >
                  {t("detail.actions.assignRole")}
                </Button>
              )}
            </Group>
            {user.roles.length === 0 ? (
              <Text c="dimmed" size="sm">
                {t("detail.noRole")}
              </Text>
            ) : (
              <Stack gap="xs">
                {user.roles.map((role) => (
                  <Group key={role} justify="space-between" wrap="nowrap">
                    <Badge
                      size="lg"
                      variant="light"
                      color={role === "Administrator" ? "grape" : "blue"}
                    >
                      {role}
                    </Badge>
                    {canManage && isAssignable(role) && (
                      <Tooltip label={t("detail.actions.removeRole")}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          loading={removingRole}
                          onClick={() => confirmRemoveRole(role)}
                          aria-label={t("detail.actions.removeRole")}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
            {user.roles.includes("Administrator") && (
              <Text size="xs" c="dimmed">
                {t("detail.adminLocked")}
              </Text>
            )}
          </Stack>
        </Card>

        <Card withBorder padding="lg">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <FiBriefcase aria-hidden="true" />
                <Title order={4}>{t("detail.sections.organizations")}</Title>
              </Group>
              {canManage && (
                <Button
                  size="xs"
                  leftSection={<FiPlus aria-hidden="true" />}
                  onClick={openOrgModal}
                >
                  {t("detail.actions.assignOrg")}
                </Button>
              )}
            </Group>
            {user.orgMemberships.length === 0 ? (
              <Text c="dimmed" size="sm">
                {t("detail.noOrgs")}
              </Text>
            ) : (
              <Stack gap="xs">
                {user.orgMemberships.map((m) => (
                  <Group key={m.orgId} justify="space-between" wrap="nowrap">
                    <Text size="md">{m.orgName}</Text>
                    {canManage && (
                      <Tooltip label={t("detail.actions.removeOrg")}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="md"
                          loading={removingOrg}
                          onClick={() => confirmRemoveOrg(m.orgId, m.orgName)}
                          aria-label={t("detail.actions.removeOrg")}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      <AssignRoleModal user={user} opened={roleModalOpened} onClose={closeRoleModal} />
      <AssignOrgMembershipModal user={user} opened={orgModalOpened} onClose={closeOrgModal} />
    </Stack>
  );
}

interface DetailFieldProps {
  label: string;
  value: string;
  mono?: boolean;
}

function DetailField({ label, value, mono }: DetailFieldProps) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="sm" {...(mono ? { ff: "monospace" as const } : {})}>
        {value}
      </Text>
    </Stack>
  );
}
